// @flow
/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['last'] }] */

import * as React from 'react';
import { useMutation } from 'urql';
import { connect } from 'react-redux';

import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import useLastChangedValue from '@ta/common.use-last-changed';
import { Icon } from '@ta/styleguide.icon';
import { type LaunchToast, Toaster } from '@ta/overlays.toast';
import { SocialLoginGate } from '@ta/social.login-gate';
import { InteractionLogger } from '@ta/tracking.interactions';
import { NativeBridge } from '@ta/common.webview';
import type { SocialObjectReferenceGql } from '@ta/social.types';
import { SocialObjectTypeMap } from '@ta/social.types';

import socialStyles from './SocialAction.less';

import AddLike from './AddLike.gql';
import DeleteLike from './DeleteLike.gql';
import useUpdatedLikeStats from './useUpdatedLikeStats';

export type ExternalProps = {|
  item: any,
  socialObject?: SocialObjectReferenceGql,
  /** optional click handler. Fired when the like action is clicked. */
  onClick?: () => mixed,
|};

export type Props = {|
  ...ExternalProps,
  loggedInUserId: string,
  csrfToken: string,
|};

const legacyTypes = new Set(['Review', 'Photo', 'Video']);

const legacyVote = (item, csrfToken) => {
  // Note: post params are in url because they aren't parsed correctly if they are in the body
  let url;
  if (item.__typename === 'Review') {
    url = `/RateUserReview?src=${encodeURIComponent(item.id)}&geo=${encodeURIComponent(
      item.location.parent.parentGeoId,
    )}&uid=${encodeURIComponent(item.userId)}&rateValue=1&token=${encodeURIComponent(csrfToken)}`;
  } else {
    let locationId;
    if (item.__typename === 'Video') {
      locationId =
        item.orderedLocations.length > 0 && item.orderedLocations[0] // Can come back as a single null!
          ? item.orderedLocations[0].locationId
          : '';
    } else {
      ({ locationId } = item.location);
    }
    url = `/VideoController?a=vt&ff=${encodeURIComponent(
      item.id,
    )}&vote=5&detail=${encodeURIComponent(locationId)}&token=${encodeURIComponent(csrfToken)}`;
  }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
  });
};

/**
 * Display Helpful Votes
 */
const LikeActionPresentation = (props: Props) => {
  const socialObject = props.socialObject
    ? props.socialObject
    : {
        objectType: SocialObjectTypeMap[props.item.__typename],
        objectId: String(props.item.id),
      };

  /** Tracks the local state of the follow button's active state for a snappier experience. */
  const [localCache, setLocalCache] = React.useState(null);

  const updatedLikeStats = useUpdatedLikeStats(socialObject);
  const dataFromProps =
    props.item && props.item.socialStatistics && props.item.socialStatistics.isLiked;

  React.useEffect(() => {
    setLocalCache(null);
  }, [updatedLikeStats]);

  let isActive = useLastChangedValue(dataFromProps, (updatedLikeStats || {}).isLiked);
  isActive = localCache === null ? isActive : localCache;

  const [, addLikeMutation] = useMutation(AddLike);
  const [, deleteLikeMutation] = useMutation(DeleteLike);

  const memberGeneratedAction = () => {
    if (isActive) {
      return deleteLikeMutation(socialObject);
    }
    const { item } = props;
    // Need to call old code path for Photos, Reviews, and Videos
    if (legacyTypes.has(item.__typename)) {
      legacyVote(item, props.csrfToken);
    }
    return addLikeMutation(socialObject);
  };

  const afterActionComplete = (launchToast: LaunchToast) => (result: any) => {
    if (result?.addLike?.statusType === 'NO_PERMISSION') {
      launchToast({
        node: localize('social_no_permission'),
      });
    }
  };

  const typeName = props.item.__typename;
  const likeObjectId = props.item.id;
  const socialCTA = localize('social_Helpful'); // Helpful
  const action = (
    <SocialLoginGate pid={40469}>
      {loginGate => {
        const interactionType = isActive ? 'unhelpful' : 'helpful';
        return (
          <InteractionLogger>
            {logLike => (
              <Toaster>
                {launchToast => (
                  <span
                    className={classNames(socialStyles.social_action, {
                      [socialStyles.active]: isActive,
                    })}
                    onClick={e => {
                      if (props.onClick) {
                        props.onClick();
                      }

                      logLike(e, interactionType);

                      if (
                        NativeBridge.isNativeWebview() &&
                        NativeBridge.isActionSupported('like', 1.0)
                      ) {
                        if (likeObjectId && typeName) {
                          NativeBridge.postToBridge('like', 1.0, {
                            objectId: likeObjectId,
                            type: typeName,
                            undo: isActive,
                          });
                        }
                      } else {
                        if (isActive) {
                          // Unlike
                          setLocalCache(false);
                        } else {
                          // Like
                          setLocalCache(true);
                        }
                        loginGate()
                          .then(memberGeneratedAction)
                          .then(afterActionComplete(launchToast));
                      }
                    }}
                  >
                    <Icon name="thumbs-up-fill" className={socialStyles.icon} />
                    <span className={`ui_link ${socialStyles.text}`}>{socialCTA}</span>
                  </span>
                )}
              </Toaster>
            )}
          </InteractionLogger>
        );
      }}
    </SocialLoginGate>
  );

  return (
    <React.Fragment>{featureIsEnabled('social_likes_enabled') ? action : null}</React.Fragment>
  );
};

const LikeAction = connect<Props, ExternalProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  csrfToken: state.auth.csrfToken,
}))(LikeActionPresentation);

export default LikeAction;
